import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { roleAndPermissionService } from "@/services/breeze/RoleAndPermissionService";
import { usePermissions } from "@/hooks/useCommonQueries";
import { BasicTable, Column } from "@/components/ui/BasicTable";
import { Form, Formik, FormikProps } from "formik";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { SkeletonPage } from "@/components/ui/Skeleton";
import CheckboxField from "@/components/form/CheckboxField";
import InputField from "@/components/form/InputField";
import PageTemplate from "@/components/page-template/PageTemplate";
import { useAuth } from "@/providers/AuthProvider";
import RoleFieldAccessDrawer from "@/components/role-field-access/RoleFieldAccessDrawer";
import {
  Box,
  Card as RadixCard,
  Flex,
  Grid,
  Heading,
  Button,
  Text,
  IconButton,
} from "@radix-ui/themes";
import { Settings } from "lucide-react";
import ErrorCard from "@/components/ui/ErrorCard";

type CrudAction = "read" | "create" | "update" | "delete";

type FlattenedPermission = Record<CrudAction, boolean> & {
  name: string;
  entity_id?: number;
};

// A checkbox for an action the API never seeded would be silently dropped on save.
const crudColumns: Column<FlattenedPermission>[] = (
  ["read", "create", "update", "delete"] as CrudAction[]
).map((action) => ({
  header: action.charAt(0).toUpperCase() + action.slice(1),
  accessor: action,
  cellRenderer: (permission) =>
    permission[action] ? (
      <CheckboxField
        name={`permissions.${permission.name}:${action}`}
        marginBottom={false}
      />
    ) : null,
}));

const EditRolePage = withTenantAdminRequired(function EditRolePage() {
  const formRef = useRef<FormikProps<any>>(null);
  const params = useParams();
  const queryClient = useQueryClient();
  const { currentTenant, safeNavigate } = useAuth();
  const [fieldAccessDrawer, setFieldAccessDrawer] = useState<{
    isOpen: boolean;
    entityId: number | null;
    entityName: string;
  }>({ isOpen: false, entityId: null, entityName: "" });

  const {
    data: permissions,
    isLoading: isPermissionsLoading,
    error: permissionsError,
  } = usePermissions();

  const {
    data: role,
    isLoading: isRoleLoading,
    error: roleError,
  } = useQuery({
    queryKey: ["role", params.roleId],
    queryFn: async () => {
      const roleId = parseInt(params.roleId as string);
      return roleAndPermissionService.getRole(roleId);
    },
  });

  const { mutate: updateRole, isPending: isUpdatingRole } = useMutation({
    mutationFn: (data: {
      name: string;
      description: string;
      frontend_url: string;
      is_active: boolean;
      anyapi_access: boolean;
      permission_ids: number[];
    }) =>
      roleAndPermissionService.updateRole(
        parseInt(params.roleId as string),
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["role", params.roleId],
      });
      safeNavigate(`/org/${currentTenant?.id}/settings/roles-and-permissions`);
    },
  });

  const getFlattenedPermissions = (anythinkOnly: boolean = false) => {
    const groupedPermissions: Record<string, FlattenedPermission> = {};

    permissions
      ?.filter((p) => (anythinkOnly ? p.is_system : !p.is_system))
      .forEach((permission) => {
        const [entity, action] = permission.name.split(":");

        if (!groupedPermissions[entity]) {
          groupedPermissions[entity] = {
            name: entity,
            read: false,
            create: false,
            update: false,
            delete: false,
            entity_id: permission.entity_id,
          };
        }

        // Update entity_id if it's available on this permission
        if (permission.entity_id && !groupedPermissions[entity].entity_id) {
          groupedPermissions[entity].entity_id = permission.entity_id;
        }

        switch (action) {
          case "read":
            groupedPermissions[entity].read = true;
            break;
          case "create":
            groupedPermissions[entity].create = true;
            break;
          case "update":
            groupedPermissions[entity].update = true;
            break;
          case "delete":
            groupedPermissions[entity].delete = true;
            break;
        }
      });

    return Object.values(groupedPermissions).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  };

  const handleOpenFieldAccess = (entityName: string, entityId: number) => {
    setFieldAccessDrawer({
      isOpen: true,
      entityId,
      entityName,
    });
  };

  const handleCloseFieldAccess = () => {
    setFieldAccessDrawer({
      isOpen: false,
      entityId: null,
      entityName: "",
    });
  };

  if (isRoleLoading || isPermissionsLoading) {
    return <SkeletonPage title="Role" />;
  }
  if (roleError || permissionsError) return <ErrorCard />;

  if (!role) {
    return (
      <Box p="4">
        <Text size="2" color="gray">
          No data found
        </Text>
      </Box>
    );
  }

  return (
    <PageTemplate
      title={`Edit ${role.name} Role`}
      rightContent={
        <Button
          type="button"
          loading={isUpdatingRole}
          onClick={() => formRef.current?.submitForm()}
        >
          Update Role
        </Button>
      }
    >
      <Formik
        innerRef={formRef}
        initialValues={{
          name: role.name,
          description: role.description || "",
          frontend_url: role.frontend_url || "",
          is_active: role.is_active,
          anyapi_access: role.anyapi_access,
          permissions: role.permissions.reduce(
            (acc: Record<string, boolean>, p) => {
              acc[p.name] = true;
              return acc;
            },
            {},
          ),
        }}
        onSubmit={(values) => {
          const permissionIds = permissions
            ?.map((p) => {
              if (values.permissions[p.name]) {
                return p.id;
              } else {
                return null;
              }
            })
            .filter((p) => p !== null);

          updateRole({
            name: values.name,
            description: values.description,
            frontend_url: values.frontend_url,
            is_active: values.is_active,
            anyapi_access: values.anyapi_access,
            permission_ids: permissionIds ?? [],
          });
        }}
      >
        {({ values }) => (
          <Form>
            <Flex direction="column" gap="6">
              <Grid columns={{ initial: "1", md: "2" }} gap="6">
                {/* Role Details */}
                <RadixCard>
                  <Box p="2">
                    <Heading as="h2" size="4" weight="medium" mb="4">
                      Role Details
                    </Heading>
                    <Flex direction="column" gap="4">
                      <InputField
                        name="name"
                        label="Role Name"
                        required
                        options={{
                          placeholder: "Enter role name",
                        }}
                      />

                      <InputField
                        name="description"
                        label="Description"
                        options={{
                          placeholder: "Enter role description",
                        }}
                      />

                      <CheckboxField name="is_active" label="Active" />

                      <CheckboxField name="anyapi_access" label="API Access" />

                      <InputField
                        name="frontend_url"
                        label="External Application URL"
                        description="The URL of your external application that users with this role should be redirected to."
                        options={{
                          placeholder: "Enter application URL",
                        }}
                      />
                    </Flex>
                  </Box>
                </RadixCard>

                {/* Permissions */}
                <RadixCard>
                  <Flex direction="column" gap="4" p="2">
                    <Heading as="h2" size="4" weight="medium">
                      Data Model Permissions
                    </Heading>
                    <Text size="2" color="gray">
                      Permissions for accessing your configured data model.
                    </Text>

                    {values.anyapi_access && (
                      <BasicTable
                        columns={[
                          { header: "Name", accessor: "name" },
                          ...crudColumns,
                          {
                            header: "Field Access",
                            accessor: "entity_id",
                            cellRenderer: (permission) =>
                              permission.entity_id ? (
                                <IconButton
                                  size="1"
                                  variant="ghost"
                                  color="gray"
                                  type="button"
                                  onClick={() =>
                                    handleOpenFieldAccess(
                                      permission.name,
                                      permission.entity_id!,
                                    )
                                  }
                                  title={`Configure field access for ${permission.name}`}
                                >
                                  <Settings
                                    style={{ width: "14px", height: "14px" }}
                                  />
                                </IconButton>
                              ) : null,
                          },
                        ]}
                        data={getFlattenedPermissions()}
                      />
                    )}

                    {!values.anyapi_access && (
                      <Text size="2" color="gray">
                        This role does not have API access therefore is not able
                        to have permissions to access entities via the API.
                      </Text>
                    )}
                  </Flex>

                  <br />

                  <Flex direction="column" gap="4" p="2">
                    <Heading as="h2" size="4" weight="medium">
                      Anythink Permissions
                    </Heading>
                    <Text size="2" color="gray">
                      {" "}
                      Permissions for accessing Anythink functionality.
                    </Text>

                    <BasicTable
                      columns={[
                        { header: "Name", accessor: "name" },
                        ...crudColumns,
                      ]}
                      data={getFlattenedPermissions(true)}
                    />

                    <CheckboxField
                      label="Allow triggering workflows"
                      name="permissions.anythink_workflows:trigger"
                      marginBottom={false}
                    />
                  </Flex>
                </RadixCard>
              </Grid>
            </Flex>
          </Form>
        )}
      </Formik>

      {/* Field Access Drawer */}
      {fieldAccessDrawer.entityId && (
        <RoleFieldAccessDrawer
          isOpen={fieldAccessDrawer.isOpen}
          onClose={handleCloseFieldAccess}
          roleId={parseInt(params.roleId as string)}
          entityId={fieldAccessDrawer.entityId}
          entityName={fieldAccessDrawer.entityName}
        />
      )}
    </PageTemplate>
  );
});

export default EditRolePage;
